import React, { useState, useEffect } from "react";
import { FileDown, FileSpreadsheet, FileText, Building2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { apiRequest, API_BASE } from "../lib/utils";
import { toast } from "sonner";

export const ReportsPage = () => {
  const [selectedFormat, setSelectedFormat] = useState("excel");
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const toggleBranch = (branchId) => {
    if (selectedBranches.includes(branchId)) {
      setSelectedBranches(selectedBranches.filter(id => id !== branchId));
    } else {
      setSelectedBranches([...selectedBranches, branchId]);
    }
  };

  const selectAllBranches = () => {
    if (selectedBranches.length === branches.length) {
      setSelectedBranches([]);
    } else {
      setSelectedBranches(branches.map(b => b.id));
    }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };

  const reports = [
    {
      id: "cre-performance",
      name: "CRE Performance",
      description: "Call logs and performance metrics by CRE",
      endpoint: "/reports/cre-performance",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "branch-performance",
      name: "Branch Performance",
      description: "Lead conversion and performance by branch",
      endpoint: "/reports/branch-performance",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "due-customers",
      name: "Due Customers",
      description: "List of customers due for service",
      endpoint: "/reports/customers/due",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "overdue-customers",
      name: "Overdue Customers",
      description: "List of overdue customers",
      endpoint: "/reports/customers/overdue",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "delayed-customers",
      name: "Delayed Customers",
      description: "List of customers with delayed service schedules",
      endpoint: "/reports/customers/delayed",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "no-show-customers",
      name: "No Show Customers",
      description: "List of customers who missed appointments",
      endpoint: "/reports/customers/no_show",
      icon: FileSpreadsheet,
      supportsBranch: true
    },
    {
      id: "complaints",
      name: "Complaints Report",
      description: "All customer complaints",
      endpoint: "/reports/complaints",
      icon: FileSpreadsheet,
      supportsBranch: false
    }
  ];

  const handleExport = async (report) => {
    setLoadingReportId(report.id);
    try {
      const token = localStorage.getItem("token");
      
      // Build URL with branch filter
      let url = `${API_BASE}${report.endpoint}?format=${selectedFormat}`;
      if (report.supportsBranch && selectedBranches.length > 0) {
        url += `&branch_ids=${selectedBranches.join(",")}`;
      }
      
      if (selectedFormat === "excel") {
        // Download file
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error("Failed to generate report");
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${report.id}_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        
        toast.success("Report downloaded successfully");
      } else {
        // JSON preview
        let jsonUrl = `${report.endpoint}?format=json`;
        if (report.supportsBranch && selectedBranches.length > 0) {
          jsonUrl += `&branch_ids=${selectedBranches.join(",")}`;
        }
        const data = await apiRequest(jsonUrl);
        console.log("Report data:", data);
        toast.success(`Report generated with ${Array.isArray(data) ? data.length : 0} records`);
      }
    } catch (error) {
      toast.error(error.message || "Failed to generate report");
    } finally {
      setLoadingReportId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Reports</h1>
        <p className="text-muted-foreground">Generate and export business reports</p>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Export Format:</Label>
              <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                <SelectTrigger className="w-[180px]" data-testid="format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel (.xlsx)
                    </div>
                  </SelectItem>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      JSON (Preview)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Multi-Branch Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <Label className="font-medium">Filter by Branches (Multi-select)</Label>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAllBranches}
                data-testid="select-all-branches"
              >
                {selectedBranches.length === branches.length ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
              {branches.length === 0 ? (
                <p className="text-sm text-muted-foreground">Loading branches...</p>
              ) : (
                branches.map((branch) => (
                  <div
                    key={branch.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                      selectedBranches.includes(branch.id)
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-background hover:bg-muted"
                    }`}
                    onClick={() => toggleBranch(branch.id)}
                    data-testid={`branch-filter-${branch.id}`}
                  >
                    <Checkbox
                      checked={selectedBranches.includes(branch.id)}
                      onCheckedChange={() => toggleBranch(branch.id)}
                      className="pointer-events-none"
                    />
                    <span className="text-sm">{branch.name}</span>
                  </div>
                ))
              )}
            </div>
            {selectedBranches.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{selectedBranches.length} branch(es) selected</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2"
                  onClick={() => setSelectedBranches([])}
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </div>
            )}
            {selectedBranches.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No branches selected - reports will include all branches
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((report) => (
          <Card key={report.id} className="bento-card" data-testid={`report-card-${report.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-lg bg-muted">
                  <report.icon className="w-5 h-5 text-muted-foreground" />
                </div>
                {report.supportsBranch && (
                  <Badge variant="outline" className="text-xs">
                    <Building2 className="w-3 h-3 mr-1" />
                    Branch Filter
                  </Badge>
                )}
              </div>
              <CardTitle className="font-heading text-lg mt-3">{report.name}</CardTitle>
              <CardDescription>{report.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => handleExport(report)}
                disabled={loadingReportId === report.id}
                className="w-full"
                data-testid={`export-${report.id}`}
              >
                <FileDown className="w-4 h-4 mr-2" />
                {loadingReportId === report.id ? "Generating..." : "Export Report"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Stats */}
      <Card className="bento-card">
        <CardHeader>
          <CardTitle className="font-heading">Report Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• <strong>CRE Performance:</strong> Shows call volumes, outcomes, and conversion rates per CRE</li>
            <li>• <strong>Branch Performance:</strong> Lead-to-customer conversion metrics by branch</li>
            <li>• <strong>Customer Status Reports:</strong> Lists customers in specific lifecycle stages</li>
            <li>• <strong>Complaints Report:</strong> All registered complaints with resolution status</li>
            <li>• <strong>Multi-Branch Filter:</strong> Select multiple branches to include in the report</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
